import { useMemo } from 'react';
import { isToolsPage } from '../../../../../utils/routing/route-utils';
import { useUserState } from '../../../../../query/UserProvider';
import { extractJobsFromUserState } from '../../../../../utils/user-state/extract-jobs-from-user-state';
import { type ApplicationState } from '../../../../../types/tracked-job';
import type { DisplayJob } from '../../../../../types';

export interface JobState {
  jobId: string | undefined;
  isSaved: boolean;
  isApplied: boolean;
  isHidden: boolean;
  currentApplicationState: ApplicationState | null;
  isToolsPage: boolean;
}

export interface UseJobStateProps {
  displayJob: DisplayJob | null | undefined;
}

/** Normalize job id to string so comparisons with the user-state derived arrays are reliable. */
function normalizeJobId(value: string | number | undefined): string | undefined {
  if (value === undefined || value === null) return undefined;
  const s = String(value);
  return s === '' ? undefined : s;
}

/**
 * Hook to get the current state of a job (saved, applied, hidden, applicationState)
 *
 * TEC-404: derives saved/applied/hidden/applicationState from the TanStack `userState` cache (via
 * `extractJobsFromUserState`, the same pure derivation `redux/createStore.ts` uses for SSR
 * hydration) instead of the Redux `jobs` slice — see `useHitState.ts` for the SRP-side twin of
 * this hook and the same reasoning. `savedJobs` reads from here too now: its writer
 * (`hooks/useSavedJobActions.ts`) dual-writes into this cache as well as Redux, so a real save +
 * reload shows up here correctly.
 */
export function useJobState({ displayJob }: UseJobStateProps): JobState {
  const rawId = displayJob?.objectID;
  const jobId = normalizeJobId(rawId as string | number | undefined);

  const { data: userStateData } = useUserState();
  const { viewedJobs, savedJobs, appliedJobs } = useMemo(
    () => extractJobsFromUserState(userStateData ?? {}),
    [userStateData]
  );

  const isSaved = useMemo(() => {
    if (!jobId) return false;
    return savedJobs.some((j) => String(j.id) === jobId);
  }, [jobId, savedJobs]);

  const isApplied = useMemo(() => {
    if (!jobId) return false;
    return appliedJobs.some((j) => String(j.id) === jobId);
  }, [jobId, appliedJobs]);

  const allJobs = useMemo(() => [...viewedJobs, ...savedJobs, ...appliedJobs], [viewedJobs, savedJobs, appliedJobs]);

  const currentApplicationState = useMemo(() => {
    if (!jobId) return null;
    const job = allJobs.find((j) => String(j.id) === jobId);
    return (job?.applicationState ?? null) as ApplicationState | null;
  }, [jobId, allJobs]);

  const isHidden = useMemo(() => {
    if (!jobId) return false;
    const job = allJobs.find((j) => String(j.id) === jobId);
    return job?.isHidden ?? false;
  }, [jobId, allJobs]);

  // Check if this is a tools page
  const isToolsPageValue = useMemo(() => {
    if (typeof window === 'undefined') return false;
    try {
      const pathname = window.location.pathname;
      return isToolsPage(pathname);
    } catch {
      return false;
    }
  }, []);

  return {
    jobId,
    isSaved,
    isApplied,
    isHidden,
    currentApplicationState,
    isToolsPage: isToolsPageValue,
  };
}
